import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

const BackgroundHearts = () => {
  const [hearts, setHearts] = useState([]);
  const heartsCreated = useRef(false);
  const resizeTimeoutRef = useRef(null);

  // Calculate optimal number of hearts based on screen size
  useEffect(() => {
    const calculateHeartCount = () => {
      const width = window.innerWidth;
      // Significantly increased number of hearts for better visibility
      return Math.max(20, Math.min(40, Math.floor(width / 40)));
    };

    const createHearts = () => {
      // Only create hearts if they haven't been created yet or during a legitimate resize
      if (!heartsCreated.current) {
        const heartsCount = calculateHeartCount();
        
        const newHearts = Array.from({ length: heartsCount }, (_, i) => ({
          id: `heart-${i}-${Date.now()}`, // Unique ID to prevent re-renders
          emoji: Math.random() > 0.5 ? '❤️' : Math.random() > 0.5 ? '💕' : '💗',
          left: Math.random() * 100,
          top: Math.random() * 100,
          size: Math.random() * 30 + 20, // Even larger hearts (20-50px)
          opacity: Math.random() * 0.4 + 0.5, // Higher opacity (0.5-0.9)
          animationDelay: Math.random() * 5,
          animationDuration: Math.random() * 5 + 10, // Animation duration
          zIndex: 0 // Changed from -1 to 0 to ensure visibility
        }));

        setHearts(newHearts);
        heartsCreated.current = true;
      }
    };

    // Create hearts initially
    createHearts();

    // Handle resize with debounce to prevent unnecessary re-renders
    const handleResize = () => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      // Only recreate hearts on significant resize events (with debounce)
      resizeTimeoutRef.current = setTimeout(() => {
        const oldWidth = window.innerWidth;
        const oldHeartCount = calculateHeartCount();
        
        // Check if we need to adjust the number of hearts significantly
        const newWidth = window.innerWidth;
        const newHeartCount = Math.max(20, Math.min(40, Math.floor(newWidth / 40)));
        
        // Only recreate hearts if there's a significant change in viewport size
        // This prevents recreation during small viewport changes like mobile scrolling
        if (Math.abs(oldWidth - newWidth) > 100 || Math.abs(oldHeartCount - newHeartCount) > 5) {
          heartsCreated.current = false; // Allow recreation
          createHearts();
        }
      }, 300); // Debounce for 300ms
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="hearts-background fixed inset-0 pointer-events-none overflow-hidden will-change-transform">
      {hearts.map((heart) => (
        <motion.div
          key={heart.id}
          className="heart-bg absolute"
          initial={{ 
            opacity: 0,
            scale: 0.5 
          }}
          animate={{ 
            opacity: heart.opacity,
            scale: 1,
            y: [0, -20, 0, -12, 0], // Increased movement range
            x: [0, 12, 0, -12, 0]   // Increased movement range
          }}
          transition={{ 
            y: { 
              repeat: Infinity, 
              duration: heart.animationDuration,
              ease: "easeInOut" 
            },
            x: {
              repeat: Infinity,
              duration: heart.animationDuration * 0.7,
              ease: "easeInOut"
            },
            opacity: { duration: 1.5 },
            scale: { duration: 1 }
          }}
          style={{
            left: `${heart.left}%`,
            top: `${heart.top}%`,
            fontSize: `${heart.size}px`,
            filter: "drop-shadow(0 0 5px rgba(255, 105, 180, 0.8))", // Stronger pink glow effect
            zIndex: heart.zIndex,
            willChange: 'transform', // Optimize for animation performance
            transform: 'translateZ(0)' // Force GPU acceleration
          }}
        >
          {heart.emoji}
        </motion.div>
      ))}
    </div>
  );
};

export default BackgroundHearts;